import type { DefaultError, Mutation, Query } from "@tanstack/react-query";
import {
  hashKey,
  MutationCache,
  QueryCache,
  QueryClient
} from "@tanstack/react-query";

import { isFetchError, type FetchError } from "@repo/api-sdk";
import { customersQueryKeys } from "./customer/queryKeys";
import type { ErrorHandlerMap } from "./models";

function globalErrorHandler(error: FetchError) {
  console.error(error);
}

function isLoggedOutSignal(
  query: Query<unknown, unknown, unknown>,
  error: FetchError
) {
  return (
    error.status === 401 &&
    query.queryHash === hashKey(customersQueryKeys.profile())
  );
}

function hasErrorHandlerMap<T extends unknown>(
  errors: T
): errors is T & ErrorHandlerMap {
  return Object.prototype.toString.call(errors) === "[object Object]";
}

// Enhanced query client with mutation error tracking
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: DefaultError, query: Query<unknown, unknown, unknown>) => {
      // Handle query errors generically
      if (isFetchError(error)) {
        // A 401 on the profile is the app's "logged out" signal, not a fault:
        // the auth route and `AuthRouterObserver` act on it, and logging it
        // would put an error in the console of every logged-out visit.
        if (isLoggedOutSignal(query, error)) return;
        // TODO add error handler map
        globalErrorHandler(error);
      }
    }
  }),

  mutationCache: new MutationCache({
    onError: (
      error: DefaultError,
      variables: unknown,
      context: unknown,
      mutation: Mutation<unknown, unknown, unknown>
    ) => {
      if (isFetchError(error)) {
        if (hasErrorHandlerMap(mutation.meta?.errors)) {
          // Type-safe access to error.response?.status and index signature
          const status = (
            error as FetchError & { response?: { status?: number } }
          ).response?.status;
          const errorHandler =
            status && typeof mutation.meta.errors === "object"
              ? mutation.meta.errors[status as keyof ErrorHandlerMap]
              : undefined;
          if (typeof errorHandler === "function") {
            errorHandler(error, variables, context);
          } else {
            globalErrorHandler(error);
          }
        } else {
          globalErrorHandler(error);
        }
      }
    }
  }),

  defaultOptions: {
    queries: {
      // Standard retry logic for queries (queries are not MFA protected)
      retry: 3,
      // Cap the backoff. React Query's default doubles without limit, so the
      // third retry alone waited 4s and a failing query spent ~7s looking like
      // a hang before it surfaced an error. Capped, the worst case is ~6s with
      // predictable gaps, and the resilience of three attempts is unchanged.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000)
    },
    mutations: {
      // Mutations use custom retry logic via MFA flow in client.ts
      // Keep React Query retry disabled to avoid conflicts
      retry: false
    }
  }
});
